import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './ProjectMembers.css';
import {
  get_project_members,
  add_project_member,
  remove_project_member,
  resolve_user_by_email,
  sync_project_pins_to_bigquery,
  refresh_ies_enrichment,
} from '../../../utils/services';

// ---------------------------------------------------------------------------
// Miembros del proyecto — quién está y POR QUÉ está.
//
// Un usuario llega a un proyecto por una o varias puertas, y hasta ahora no había
// dónde verlas juntas: los pines se veían en su pestaña, la lista de Brevo en la
// suya, y el TOMi vinculado no se veía en ninguna parte. Cuando alguien existía en
// una población y no en la otra el dashboard mostraba media verdad sin avisar.
//
//   PIN     redimió un pin del proyecto                    derivada
//   TOMi    tiene un TOMi del proyecto vinculado           derivada
//   Lista   está en la lista del proyecto (users_by_project) derivada
//   Manual  alguien lo agregó aquí                         ALMACENADA
//
// Solo la manual se puede quitar, y no por una regla nuestra: las otras tres son el
// reflejo de otra tabla, así que no hay fila que borrar y volvería en el siguiente
// request. Para sacar a alguien hay que ir a la referencia — quitarle el pin,
// reasignar el TOMi o cambiar la lista.
//
// Por qué existe la puerta manual: de los pines repartidos, casi la mitad nunca se
// redime. Al maestro le regalan el pin y no lo estrena porque ya tiene licencia
// activa por otro lado; el proyecto lo cuenta como suyo y el dashboard no lo ve.
// ---------------------------------------------------------------------------

const ORIGIN_LABEL = {
  pin: 'Por PIN',
  tomi: 'Por TOMi',
  list: 'Por lista',
  manual: 'Manual',
};

const ORIGIN_HINT = {
  pin: 'Redimió un pin de este proyecto. Para sacarlo, quítele el pin.',
  tomi: 'Tiene un TOMi de este proyecto vinculado. Para sacarlo, reasigne el TOMi.',
  list: 'Está en la lista del proyecto (Brevo o el sync de pines). Para sacarlo, cambie la lista.',
  manual: 'Se agregó a mano en esta pantalla. Es el único caso que se puede quitar desde aquí.',
};

/** Orden estable de las insignias: de la puerta más "fuerte" a la más circunstancial. */
const ORIGIN_ORDER = ['pin', 'tomi', 'list', 'manual'];

const PAGE = 200;

function ProjectMembers({ token, projectId, canEdit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(PAGE);

  // Alta por email
  const [email, setEmail] = useState('');
  const [candidates, setCandidates] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null); // { type: 'ok'|'error', text }
  const [reconciling, setReconciling] = useState(false);
  const [steps, setSteps] = useState([]); // [{ k, label, state: 'run'|'ok'|'fail' }]

  const load = useCallback(async () => {
    if (!token || !projectId) return;
    setLoading(true);
    setError(null);
    const res = await get_project_members(token, projectId);
    setLoading(false);
    if (res == null) {
      setError('No se pudo cargar la lista. Puede ser la sesión o que el dashboard no responda.');
      return;
    }
    setData(res);
  }, [token, projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const members = useMemo(() => data?.members ?? [], [data]);
  const summary = data?.summary ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter(
      (m) =>
        (m.name || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q) ||
        (m.institution?.name || '').toLowerCase().includes(q) ||
        (m.pins || []).some((p) => p.toLowerCase().includes(q)) ||
        (m.serials || []).some((s) => s.toLowerCase().includes(q))
    );
  }, [members, query]);

  useEffect(() => {
    setLimit(PAGE);
  }, [query]);

  const searchEmail = async () => {
    const value = email.trim();
    if (!value) return;
    setBusy(true);
    setNotice(null);
    setCandidates(null);
    // Se resuelve contra musers (en vivo) por el PHP que ya existe para vincular
    // dispositivos, no contra la réplica del lab: una cuenta creada hoy ya aparece.
    const res = await resolve_user_by_email(token, projectId, value);
    setBusy(false);
    const users = res && Array.isArray(res.users) ? res.users : [];
    if (!users.length) {
      setNotice({ type: 'error', text: 'No hay ninguna cuenta de TOMi con ese correo.' });
      return;
    }
    setCandidates(users);
  };

  const doAdd = async (user) => {
    setBusy(true);
    setNotice(null);
    // Sin institución: se puede pertenecer al proyecto sin escuela conocida, y
    // obligar a poner una cualquiera es peor que no tener el dato.
    const res = await add_project_member(token, projectId, user.user_id, null);
    setBusy(false);
    if (!res.ok) {
      setNotice({ type: 'error', text: res.message });
      return;
    }
    setNotice({
      type: 'ok',
      text:
        res.result === 'already-member'
          ? `${user.user_name || user.user_email} ya pertenecía al proyecto por otra vía.`
          : `${user.user_name || user.user_email} agregado al proyecto.`,
    });
    setEmail('');
    setCandidates(null);
    load();
  };

  /**
   * Sincronizar: dejar las cuatro fuentes de acuerdo, EN ORDEN.
   *
   * El orden no es cosmético. `list_update_bigq.php` es el único paso destructivo —
   * hace DELETE del slice del proyecto en users_by_project y lo reconstruye—, así que
   * todo lo que enriquece o agrega tiene que ir DESPUÉS o se pierde:
   *
   *   1. Reconstruir el roster desde los pines  (DELETE + rebuild)
   *      └─ el propio PHP enrola de una a los vinculados por TOMi, después del rebuild
   *   2. Re-aplicar ciudad/institución del master IES  (UPDATE sobre lo reconstruido)
   *   3. Releer
   *
   * Antes esto eran tres acciones sueltas en pantallas distintas y el orden dependía de
   * que la persona lo recordara; si se hacía al revés, el paso 1 borraba el 2 y nadie
   * se enteraba —quedaba un cero plausible, no un error.
   */
  const doReconcile = async () => {
    if (
      !window.confirm(
        'Sincronizar reconstruye el roster del proyecto desde los pines, enrola a los ' +
          'vinculados por TOMi y vuelve a aplicar la institución del master.\n\n' +
          'Puede tardar varios minutos. ¿Continuar?'
      )
    )
      return;

    setReconciling(true);
    setNotice(null);
    setSteps([{ k: 'sync', label: 'Reconstruyendo el roster…', state: 'run' }]);

    const sync = await sync_project_pins_to_bigquery(token, projectId);
    const syncFailed = sync == null || sync === 'INVALID';
    setSteps([
      {
        k: 'sync',
        label: syncFailed ? 'No se pudo reconstruir el roster' : 'Roster reconstruido (TOMi incluidos)',
        state: syncFailed ? 'fail' : 'ok',
      },
      { k: 'ies', label: 'Aplicando institución del master…', state: 'run' },
    ]);

    // Se sigue aunque el sync falle: re-aplicar la institución sobre lo que haya
    // igual mejora el estado, y nunca lo empeora (es un UPDATE, no un rebuild).
    const ies = await refresh_ies_enrichment(token, projectId);
    setSteps((prev) => [
      prev[0],
      {
        k: 'ies',
        label:
          ies == null
            ? 'No se pudo aplicar la institución'
            : ies.skipped
              ? 'Sin master de instituciones para este proyecto (nada que aplicar)'
              : `Institución aplicada a ${ies.ubpUpdated} maestros`,
        state: ies == null ? 'fail' : 'ok',
      },
    ]);

    await load();
    setReconciling(false);
    setNotice({
      type: syncFailed ? 'error' : 'ok',
      text: syncFailed
        ? 'La reconstrucción falló. La lista de abajo puede estar incompleta.'
        : 'Fuentes reconciliadas.',
    });
  };

  const doRemove = async (member) => {
    const label = member.name || member.email || `Usuario #${member.userId}`;
    if (!window.confirm(`¿Quitar a ${label} del proyecto?`)) return;
    setBusy(true);
    setNotice(null);
    const res = await remove_project_member(token, projectId, member.userId);
    setBusy(false);
    if (!res.ok) {
      setNotice({ type: 'error', text: res.message });
      return;
    }
    setNotice({ type: 'ok', text: `${label} quitado del proyecto.` });
    load();
  };

  return (
    <div className="pm-wrap">
      {summary ? (
        <div className="pm-summary">
          <span className="pm-total">
            <strong>{summary.total}</strong> maestros
          </span>
          {ORIGIN_ORDER.map((o) =>
            summary.byOrigin?.[o] ? (
              <span key={o} className={`pm-chip pm-chip-${o}`} title={ORIGIN_HINT[o]}>
                {ORIGIN_LABEL[o]} · {summary.byOrigin[o]}
              </span>
            ) : null
          )}
          {summary.withoutInstitution > 0 ? (
            <span className="pm-chip pm-chip-warn" title="No tienen institución por ningún camino">
              Sin institución · {summary.withoutInstitution}
            </span>
          ) : null}
          {summary.unredeemedPins > 0 ? (
            <span
              className="pm-chip pm-chip-muted"
              title="Pines repartidos que nadie ha redimido. Si esas personas ya están en el proyecto, agréguelas aquí."
            >
              Pines sin usar · {summary.unredeemedPins}
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="pm-toolbar">
        <input
          type="text"
          className="pm-search"
          placeholder="Buscar por nombre, correo, institución, pin o serial…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {/* Dos acciones separadas a propósito: "Recargar" solo vuelve a leer (barato,
            sin efectos); "Sincronizar" escribe. Un solo botón que a veces lee y a veces
            reconstruye el roster es justo la ambigüedad que causó el problema. */}
        <button
          type="button"
          className="pm-refresh"
          onClick={load}
          disabled={loading || reconciling}
          title="Vuelve a pedirle la lista al servidor. El buscador de al lado no consulta nada: filtra lo ya cargado mientras escribes."
        >
          {loading ? 'Cargando…' : 'Recargar'}
        </button>
        {canEdit ? (
          <button
            type="button"
            className="pm-reconcile"
            onClick={doReconcile}
            disabled={reconciling || loading}
            title="Reconstruye el roster, enrola a los vinculados por TOMi y re-aplica la institución del master, en ese orden"
          >
            {reconciling ? 'Sincronizando…' : 'Sincronizar'}
          </button>
        ) : null}
      </div>

      {steps.length ? (
        <ol className="pm-steps">
          {steps.map((s) => (
            <li key={s.k} className={`pm-step pm-step-${s.state}`}>
              <span className="pm-step-mark" aria-hidden="true">
                {s.state === 'ok' ? '✓' : s.state === 'fail' ? '✕' : '·'}
              </span>
              {s.label}
            </li>
          ))}
        </ol>
      ) : null}

      {/* Que el buscador FILTRA (no consulta) tiene que verse, no explicarse: en cuanto
          hay texto se dice cuántos de cuántos quedan. */}
      {query.trim() ? (
        <div className="pm-filter-count">
          Mostrando <strong>{filtered.length}</strong> de {members.length}
        </div>
      ) : null}

      {error ? <div className="pm-notice pm-notice-error">{error}</div> : null}
      {notice ? <div className={`pm-notice pm-notice-${notice.type}`}>{notice.text}</div> : null}

      {/* El proyecto grande (p4) trae más de mil miembros y la consulta cruza MariaDB
          con BigQuery: son un par de segundos en los que la tabla se queda vacía y
          parece que no hay nadie. El velo mantiene visible lo anterior mientras carga. */}
      <div className="table-container3 pm-table-wrap">
        {loading ? (
          <div className="pm-loading" role="status" aria-live="polite">
            <span className="pm-spinner" aria-hidden="true" />
            <span>Cargando maestros…</span>
          </div>
        ) : null}
        <table className="version-table3 pm-table">
          <thead>
            <tr>
              <th style={{ width: '38%' }}>Usuario</th>
              <th style={{ width: '26%' }}>De dónde se cuenta</th>
              <th style={{ width: '28%' }}>Institución</th>
              <th style={{ width: '8%' }} />
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, limit).map((m) => (
              <tr key={m.userId}>
                <td>
                  <div className="pm-name">{m.name || `Usuario #${m.userId}`}</div>
                  <div className="pm-email">{m.email}</div>
                </td>
                <td>
                  <div className="pm-origins">
                    {ORIGIN_ORDER.filter((o) => m.origins.includes(o)).map((o) => (
                      <span key={o} className={`pm-badge pm-badge-${o}`} title={ORIGIN_HINT[o]}>
                        {ORIGIN_LABEL[o]}
                      </span>
                    ))}
                  </div>
                  {m.pins.length || m.serials.length ? (
                    <div className="pm-refs">
                      {m.pins.join(', ')}
                      {m.pins.length && m.serials.length ? ' · ' : ''}
                      {m.serials.join(', ')}
                    </div>
                  ) : null}
                </td>
                <td>
                  {m.institution ? (
                    <span title={`Resuelta por ${m.institutionSource}`}>{m.institution.name}</span>
                  ) : (
                    <span className="pm-none">Sin institución</span>
                  )}
                </td>
                <td className="pm-action">
                  {canEdit && m.removable ? (
                    <button
                      type="button"
                      className="pm-remove"
                      onClick={() => doRemove(m)}
                      disabled={busy}
                    >
                      Quitar
                    </button>
                  ) : (
                    <span
                      className="pm-locked"
                      title="Entra por otra vía: quitarlo aquí no lo sacaría del proyecto. Hay que ir a la referencia (pin, TOMi o lista)."
                    >
                      —
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {!filtered.length && !loading ? (
              <tr>
                <td colSpan={4} className="pm-empty">
                  {members.length ? 'Nadie coincide con la búsqueda.' : 'Este proyecto no tiene maestros.'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {filtered.length > limit ? (
        <button type="button" className="pm-more" onClick={() => setLimit((n) => n + PAGE)}>
          Mostrar {Math.min(PAGE, filtered.length - limit)} más ({filtered.length - limit} restantes)
        </button>
      ) : null}

      {canEdit ? (
        <div className="pm-add">
          <div className="pm-add-title">Agregar un maestro por correo</div>
          <div className="pm-add-hint">
            Para quien ya pertenece al proyecto pero no aparece — típicamente porque le dieron un
            pin y no lo redimió (ya tenía licencia activa).
          </div>
          <div className="pm-add-row">
            <input
              type="email"
              className="pm-add-input"
              placeholder="correo@ejemplo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') searchEmail();
              }}
            />
            <button type="button" className="pm-add-btn" onClick={searchEmail} disabled={busy}>
              {busy ? 'Buscando…' : 'Buscar'}
            </button>
          </div>

          {candidates ? (
            <div className="pm-candidates">
              {candidates.map((u) => (
                <div key={u.user_id} className="pm-candidate">
                  <span className="pm-candidate-name">{u.user_name || '(sin nombre)'}</span>
                  <span className="pm-candidate-email">{u.user_email}</span>
                  <button
                    type="button"
                    className="pm-candidate-btn"
                    onClick={() => doAdd(u)}
                    disabled={busy}
                  >
                    Agregar
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default ProjectMembers;
